import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { trackPurchaseConversion } from '@/utils/analytics';

/**
 * Hook to handle SetupIntent redirects from payment methods that require external authorization
 * (Revolut Pay, Klarna, 3D Secure cards, etc.)
 */
export function useSetupIntentRedirect() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    const handleRedirect = async () => {
      const paymentRedirect = searchParams.get('payment_redirect');
      const setupIntentId = searchParams.get('setup_intent');
      const clientSecret = searchParams.get('setup_intent_client_secret');
      const tier = searchParams.get('tier') || 'monthly';
      const currency = searchParams.get('currency') || 'usd';

      // Only process if this is a setup_intent redirect
      if (paymentRedirect !== 'setup_intent' || !setupIntentId || !clientSecret) {
        return;
      }

      // Prevent double processing
      if (isProcessing) return;
      setIsProcessing(true);

      console.log('[SetupIntentRedirect] Processing redirect for SetupIntent:', setupIntentId);

      try {
        // Get Stripe publishable key
        const { data: configData, error: configError } = await supabase.functions.invoke('get-stripe-config');
        
        if (configError || !configData?.publishableKey) {
          throw new Error('Failed to get Stripe configuration');
        }

        // Load Stripe and retrieve the SetupIntent
        const stripe = await loadStripe(configData.publishableKey);
        if (!stripe) {
          throw new Error('Failed to load Stripe');
        }

        const { setupIntent, error: retrieveError } = await stripe.retrieveSetupIntent(clientSecret);

        if (retrieveError) {
          throw new Error(retrieveError.message || 'Failed to retrieve payment details');
        }

        console.log('[SetupIntentRedirect] SetupIntent status:', setupIntent?.status);

        if (setupIntent?.status === 'succeeded' && setupIntent.payment_method) {
          // Start the subscription with the payment method
          console.log('[SetupIntentRedirect] Starting subscription with payment method:', setupIntent.payment_method);
          
          const { data: subData, error: subscriptionError } = await supabase.functions.invoke('start-trial-subscription', {
            body: {
              payment_method: setupIntent.payment_method,
              tier: tier,
              currency: currency
            }
          });

          // CRITICAL: Verify BOTH no error AND subscription was actually created
          if (subscriptionError || !subData?.subscription_id) {
            console.error('[SetupIntentRedirect] Subscription failed:', subscriptionError, subData);
            throw new Error(subData?.error || 'Failed to start subscription');
          }

          // Only track purchase conversion when subscription_id is confirmed
          trackPurchaseConversion({
            transaction_id: subData.subscription_id, // Use actual Stripe subscription ID
            value: 0, // Free trial
            currency: currency,
            tier: tier as 'monthly' | 'annual' | 'weekly',
            payment_method: 'card', // Use 'card' as fallback for redirect-based methods
            is_trial: true
          });

          console.log('[SetupIntentRedirect] Subscription started successfully');
          
          // Show success animation
          setShowSuccess(true);
          
          // Clean up URL parameters
          const newSearchParams = new URLSearchParams();
          newSearchParams.set('payment_success', 'true');
          setSearchParams(newSearchParams, { replace: true });

          toast({
            title: "Welcome to BUXBOX!",
            description: "Your free trial has started. Enjoy unlimited reading!",
          });

        } else if (setupIntent?.status === 'requires_payment_method') {
          throw new Error('Payment was not completed. Please try again.');
        } else if (setupIntent?.status === 'requires_action') {
          throw new Error('Additional authentication required. Please try again.');
        } else {
          throw new Error(`Unexpected payment status: ${setupIntent?.status}`);
        }

      } catch (error: any) {
        console.error('[SetupIntentRedirect] Error:', error);
        
        toast({
          title: "Payment Issue",
          description: error.message || "There was a problem processing your payment. Please try again.",
          variant: "destructive",
        });

        // Clean up URL parameters on error
        navigate('/', { replace: true });
      } finally {
        setIsProcessing(false);
      }
    };

    handleRedirect();
  }, [searchParams]); // Only depend on searchParams

  return { isProcessing, showSuccess };
}
